import { createListCollection } from '@ark-ui/react/select';
import { NoSSR } from '@ui/components/NoSSR';
import { StatLabel, StatPercent } from '@ui/components/Stats';
import { TextTooltip } from '@ui/components/TextTooltip';
import { FUNDING_RATE_DISPLAY_DECIMALS } from '@ui/constants/numbersDecimals';
import { Portal } from '@ui/lib/park/portal';
import { Select } from '@ui/lib/park/ui/select';
import { Text } from '@ui/lib/park/ui/text';
import { getSemanticPropsByNumber } from '@ui/utils/getSemanticProps';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { BoxProps, Flex, HStack } from 'styled-system/jsx';

import { useBorrowRates } from '../../../../features/Trade/hooks/useBorrowRates';

const Icon = () => (
  <svg
    style={{
      width: '10px',
      height: '10px',
      transform: 'rotate(90deg)',
    }}
    viewBox="0 0 24 24"
  >
    <path
      fill="rgba(208,219,218,0.4)"
      fillRule="evenodd"
      d="M7.417 0 20 12 7.417 24 4 20.74 13.165 12 4 3.26 7.417 0Z"
    />
  </svg>
);

const collection = createListCollection({
  items: [
    { label: '1h', value: '1' },
    { label: '8h', value: '8' },
    { label: '24h', value: '24' },
    { label: '1y', value: '8760' },
  ],
});

export const HoursSelect = (props: {
  value: string;
  onChange: (value: string) => void;
}) => {
  return (
    <NoSSR>
      <Select.Root
        w="60px"
        display="inline-flex"
        size="vsm"
        // portaled positioner has no width context — pin it to the trigger
        positioning={{ sameWidth: true }}
        onValueChange={(v) => {
          props.onChange(v.value[0]);
        }}
        value={[props.value]}
        collection={collection}
      >
        <Select.Control display="inline-flex">
          <Select.Trigger>
            <Select.ValueText />
            <Icon />
          </Select.Trigger>
        </Select.Control>
        {/* portal escapes the market bar's overflow clipping */}
        <Portal>
          <Select.Positioner>
            <Select.Content>
              <Select.ItemGroup>
                {collection.items.map((item) => (
                  <Select.Item key={item.value} item={item}>
                    <Select.ItemText>{item.label}</Select.ItemText>
                    <Select.ItemIndicator />
                  </Select.Item>
                ))}
              </Select.ItemGroup>
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
    </NoSSR>
  );
};

export const BorrowRates = (p: BoxProps) => {
  const { t } = useTranslation();
  const [selectedHour, setSelectedHour] = useState<string>('1');
  const { longFundingRatePerHour, shortFundingRatePerHour } = useBorrowRates();

  return (
    <Flex direction="column" w="150px" justifyContent="between">
      <Flex alignItems="center" gap={2}>
        <StatLabel>{t('common.Funding(L/S)', 'Funding (L/S)')}</StatLabel>
        <HoursSelect onChange={setSelectedHour} value={selectedHour} />
      </Flex>
      <HStack mt={0.5} gap={2}>
        <StatPercent
          value={longFundingRatePerHour?.times(selectedHour)}
          color={getSemanticPropsByNumber(longFundingRatePerHour).color}
          opts={{
            isShowSign: true,
            displayDecimals: FUNDING_RATE_DISPLAY_DECIMALS,
          }}
          data-cy="LongFundingRatePerHourInStat"
          data-cy-value={longFundingRatePerHour}
        />
        <Text textStyle="numbers.small" color="v2.text.subtle">{'/'}</Text>
        <StatPercent
          value={shortFundingRatePerHour?.times(selectedHour)}
          color={getSemanticPropsByNumber(shortFundingRatePerHour).color}
          opts={{
            isShowSign: true,
            displayDecimals: FUNDING_RATE_DISPLAY_DECIMALS,
          }}
          data-cy="ShortFundingRatePerHourInStat"
          data-cy-value={shortFundingRatePerHour}
        />
      </HStack>
    </Flex>
  );
};
